import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Icon from '@amzn/meridian/icon';
import Toggle from '@amzn/meridian/toggle';
import Tooltip from '@amzn/meridian/tooltip';
import Alert from '@amzn/meridian/alert';
import Toaster, { ToasterToasts } from '@amzn/meridian/toaster/toaster';
import bulbTokens from '@amzn/meridian-tokens/base/icon/bulb';
import closeLargeTokens from '@amzn/meridian-tokens/base/icon/close-large';
import closeSmallTokens from '@amzn/meridian-tokens/base/icon/close-small';
import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import { format } from 'date-fns';
import { generateCsv, downloadCsv, buildCsvFileName, CsvRow } from '@components/agile/utils/csvExport';
import { getDateInUTC } from '@components/agile/utils/GetDateUtils';
import { INDEFINITE_TIME } from '@constants/strings';
import { useSearchParams } from 'react-router-dom';
import { AgileManagementProps } from '@components/agile/AgileManagement';
import MacroplanogramSelector from '@components/selectors/MacroplanogramSelector';
import SubcategorySelector from '@components/selectors/SubcategorySelector';
import StoreSelector from '@components/selectors/ProgramStoreSelector';
import { CustomDatePicker } from '@components/controls/CustomDatePicker';
import useGetMacroplanogramResults from '@store/hooks/useGetMacroplanogramResults';
import useGetItemEntries from '@store/hooks/useGetItemEntries';
import useGetProgramStores from '@store/hooks/useGetProgramStoresResult';
import { storeSubcategory } from '@components/agile/types/AgileMacroplanogramTypes';
import AddItemsModal from 'src/components/agile/agileComponents/AddItemsModal';
import DeleteItemsModal from 'src/components/agile/agileComponents/DeleteItemsModal';
import ModifyItemsModal from 'src/components/agile/agileComponents/ModifyItemsModal';
import ItemAssortmentTable from 'src/components/agile/agileComponents/ItemAssortmentTable';
import ItemBulkUploadMain from 'src/components/agile/agileComponents/ItemBulkUploadOperation/ItemBulkUploadMain';
import ItemBulkUpload from 'src/components/agile/agileComponents/ItemBulkUploadOperation/ItemBulkUpload';
import { ItemEntryInput } from '@components/agile/types/ItemEntryTypes';
import NavigationHelper from '@components/agile/utils/URLParametersUtils';
import { useProgramConfig } from 'src/hooks/useProgramConfig';
import { MacroplanogramScope } from 'src/types/programConfig';

const ITEM_ENTRIES_QUERY_FILTER = {
  predicate: (q: { queryKey: readonly unknown[] }) => q.queryKey[0] === 'GetItemEntries.infinite',
};

const DisabledButtonWithTooltip = ({ title, label }: { title: string; label: string }) => (
  <Tooltip position="top" title={title}>
    <span tabIndex={0}>
      <Button type="secondary" disabled>
        {label}
      </Button>
    </span>
  </Tooltip>
);

const ItemAssortmentManagement = (props: AgileManagementProps) => {
  const { urlParams, setUrlParameter } = props;
  const queryClient = useQueryClient();
  const config = useProgramConfig();
  const subcategoryLabel = config.labels.subcategory;
  const itemIdentifierLabel = config.labels.itemIdentifierType;
  const scope: MacroplanogramScope = config.features.itemAssortment?.scope ?? 'subcategory';
  const [routerSearchParams, setRouterSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('viewAll');
  // Use URL search params as single source of truth for initial state
  const [storeId, setStoreId] = useState<string | undefined>(routerSearchParams.get('storeId') || undefined);
  const [date, setDate] = useState<string>(routerSearchParams.get('date') || '');
  const [macroplanogramId, setMacroplanogramId] = useState<string | undefined>(
    routerSearchParams.get('zoneId') || undefined,
  );
  const [subcategoryId, setSubcategoryId] = useState<string | undefined>(
    routerSearchParams.get('subcategoryId') || undefined,
  );
  const [includeFuture, setIncludeFuture] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [toasts, setToasts] = useState<(ToasterToasts & { children?: string; dismissAfter?: number; type?: string })[]>(
    [],
  );
  const includeDraft = true;

  // Bulk upload state lifted from ItemBulkUploadMain
  const [inputItemEntries, setInputItemEntries] = useState<ItemEntryInput[]>([]);
  const [inputTableFlag, setInputTableFlag] = useState(false);
  const [reviewTableFlag, setReviewTableFlag] = useState(false);
  const [alertFlag, setAlertFlag] = useState<boolean>(false);
  const [showBulkUploadHint, setShowBulkUploadHint] = useState(true);
  const [bulkUploadResetKey, setBulkUploadResetKey] = useState(0);

  // Sync filter state to URL and parent
  useEffect(() => {
    const params: Record<string, string> = {
      storeId: storeId || '',
      date,
      zoneId: macroplanogramId || '',
      subcategoryId: subcategoryId || '',
    };
    setRouterSearchParams(NavigationHelper.createSearchParams(params, urlParams.demo));
    setUrlParameter({
      storeId: storeId || '',
      date,
      zoneId: macroplanogramId || '',
      subcategoryId,
      demo: urlParams.demo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, date, macroplanogramId, subcategoryId]);

  const onCloseToast = useCallback((id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)), []);

  // Auto-dismiss toasts after their dismissAfter time
  const toastTimerRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  useEffect(() => {
    toasts.forEach((toast) => {
      if (toast.dismissAfter && !toastTimerRef.current.has(toast.id)) {
        const timer = setTimeout(() => onCloseToast(toast.id), toast.dismissAfter);
        toastTimerRef.current.set(toast.id, timer);
      }
    });
    // Clean up removed toasts
    toastTimerRef.current.forEach((timer, id) => {
      if (!toasts.find((t) => t.id === id)) {
        clearTimeout(timer);
        toastTimerRef.current.delete(id);
      }
    });
  }, [toasts, onCloseToast]);

  // Clean up all timers on unmount
  useEffect(() => {
    const timers = toastTimerRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const timestamp = date ? new Date(date).getTime() : 0;
  const canFetchMacro = !!storeId && !!timestamp;

  const { macroplanogramList, isLoading: isMacroLoading } = useGetMacroplanogramResults(
    {
      storeId: canFetchMacro ? storeId : '',
      timestamp: canFetchMacro ? timestamp : 0,
      includeFuture: false,
      includeDraft,
    },
    { enabled: canFetchMacro },
  );

  const selectedMacro = macroplanogramList?.find((mp) => mp.entryId === macroplanogramId);
  const subcategories = useMemo<storeSubcategory[]>(
    () => selectedMacro?.storeSubcategories ?? [],
    [selectedMacro?.storeSubcategories],
  );

  // Macroplanogram-level categoryId for Phase 1 GFC. Sourced from the
  // selected drop zone; undefined for WFM where subcategoryId scopes items.
  const macroCategoryId = scope === 'category' ? (selectedMacro?.categoryId ?? undefined) : undefined;

  const { stores } = useGetProgramStores();
  const storeLabel = useMemo(() => stores.find((s) => s.store === storeId)?.label ?? storeId ?? '', [stores, storeId]);

  const resetBulkUploadState = () => {
    setInputItemEntries([]);
    setInputTableFlag(false);
    setReviewTableFlag(false);
    setAlertFlag(false);
    setBulkUploadResetKey((prev) => prev + 1);
  };

  const disablePastDates = useCallback((dateStr: string) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return dateStr < today;
  }, []);

  const handleDownloadTemplate = useCallback(() => {
    const itemIdHeader = itemIdentifierLabel.toLowerCase();
    let csv: string;
    if (scope === 'subcategory') {
      // Subcategory-driven programs (e.g. WholeFoods).
      csv = `${itemIdHeader},${subcategoryLabel.toLowerCase()}\n123456789,Example ${subcategoryLabel}\n`;
    } else {
      // Category-driven programs (e.g. GFC): one Drop Zone per upload, scoped by
      // the selected macroplanogram, so only the item id column is needed.
      csv = `${itemIdHeader}\n123456789\n`;
    }
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_upload_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, [itemIdentifierLabel, subcategoryLabel, scope]);

  const handleStoreChange = (value: string) => {
    setStoreId(value);
    setMacroplanogramId(undefined);
    setSubcategoryId(undefined);
    setSearchEnabled(false);
    resetBulkUploadState();
  };

  const handleDateChange = (value: string) => {
    setDate(value);
    setMacroplanogramId(undefined);
    setSubcategoryId(undefined);
    setSearchEnabled(false);
    resetBulkUploadState();
  };

  const handleMacroplanogramChange = (value: string) => {
    setMacroplanogramId(value);
    setSubcategoryId(undefined);
    setSearchEnabled(false);
    resetBulkUploadState();
  };

  const handleSubcategoryChange = (value: string) => {
    setSubcategoryId(value);
  };

  // Search gate — true when page loaded with all required params in URL (jump), or user clicked Search
  const [searchEnabled, setSearchEnabled] = useState(
    () => !!routerSearchParams.get('storeId') && !!routerSearchParams.get('date') && !!routerSearchParams.get('zoneId'),
  );

  const canSearch = !!storeId && !!macroplanogramId && !!timestamp;

  const {
    itemEntriesResult,
    isLoading: isSearching,
    hasNextPage,
    fetchNextPage,
  } = useGetItemEntries(
    {
      storeId: storeId ?? '',
      macroplanogramId: macroplanogramId ?? '',
      timestamp,
      includeFuture,
      // GFC scopes items by the Drop Zone's categoryId, not subcategoryId.
      subcategoryId: scope === 'category' ? undefined : subcategoryId || undefined,
      categoryId: macroCategoryId,
    },
    { enabled: searchEnabled && canSearch },
  );

  const selectedEntries = useMemo(
    () => itemEntriesResult?.filter((e) => selectedIds.has(e.entryId)) ?? [],
    [itemEntriesResult, selectedIds],
  );

  useEffect(() => {
    setSelectedIds(new Set());
  }, [itemEntriesResult]);

  const handleSearch = () => {
    if (canSearch) {
      setSearchEnabled(true);
    }
  };

  const handleExportCsv = useCallback(() => {
    if (hasNextPage) {
      setToasts((prev) => [
        ...prev,
        {
          id: `export-partial-${Date.now()}`,
          children: 'Not all items have been loaded. Scroll to load all items before exporting.',
          type: 'warning',
          dismissAfter: 5000,
        },
      ]);
      return;
    }
    const csvRows: CsvRow[] = (itemEntriesResult ?? []).map((entry) => ({
      scancode: entry.itemId,
      title: entry.itemMetaDataEntry?.title ?? '',
      imageLink: entry.itemMetaDataEntry?.image ?? '',
      // Scope column is only emitted for subcategory-scoped programs; GFC omits it.
      scope:
        scope === 'subcategory'
          ? (subcategories.find((sc) => sc.subcategoryId === entry.subcategoryId)?.subcategoryName ?? '')
          : '',
      startTime: getDateInUTC(entry.startTime),
      endTime: entry.endTime >= INDEFINITE_TIME ? 'Indefinite' : getDateInUTC(entry.endTime),
    }));
    const departmentName = selectedMacro?.macroplanogramName ?? '';
    const csvContent = generateCsv(
      csvRows,
      { store: storeLabel, macroplanogram: departmentName },
      {
        scopeColumnLabel: subcategoryLabel,
        itemIdLabel: itemIdentifierLabel,
        includeScopeColumn: scope === 'subcategory',
      },
    );
    downloadCsv(csvContent, buildCsvFileName(storeLabel, departmentName, date));
  }, [
    itemEntriesResult,
    subcategories,
    storeLabel,
    selectedMacro?.macroplanogramName,
    date,
    hasNextPage,
    scope,
    subcategoryLabel,
    itemIdentifierLabel,
  ]);

  const handleBulkUploadSaveSuccess = useCallback(() => {
    resetBulkUploadState();
    setActiveTab('viewAll');
    setSearchEnabled(true);
    queryClient.removeQueries(ITEM_ENTRIES_QUERY_FILTER);
    setToasts((prev) => [
      ...prev,
      {
        id: `bulk-upload-success-${Date.now()}`,
        children: 'Bulk upload saved successfully.',
        type: 'success' as const,
        dismissAfter: 5000,
      },
    ]);
  }, [queryClient]);

  const handleDuplicatesRemoved = useCallback(() => {
    setToasts((prev) => [
      ...prev,
      {
        id: `dedup-${Date.now()}`,
        children: 'Duplicate entries were automatically removed.',
        type: 'informational' as const,
        dismissAfter: 5000,
      },
    ]);
  }, []);

  return (
    <Box type="outline" spacingInset="300">
      <Toaster toasts={toasts} onCloseToast={onCloseToast}>
        {(toast) => (
          <Alert toast {...toast}>
            {toast.children}
          </Alert>
        )}
      </Toaster>
      <Column alignmentHorizontal="start">
        <Heading level={2}>Agile Merchandising Dashboard</Heading>
        <TabGroup value={activeTab} onChange={setActiveTab} type="classic">
          <Tab value="viewAll" id="viewAll">
            View All
          </Tab>
          <Tab value="bulkUpload" id="bulkUpload">
            {/* A place holder for Bulk Operation */}
            Bulk Operation
          </Tab>
        </TabGroup>
        <div style={{ display: activeTab === 'viewAll' ? 'block' : 'none' }}>
          <Box spacingInset="300">
            <Column spacing="500">
              <Row alignmentVertical="bottom">
                <StoreSelector selectedStoreId={storeId} onStoreSelect={handleStoreChange} />
                <CustomDatePicker value={date} onChange={handleDateChange} label="Date" />
                <MacroplanogramSelector
                  value={macroplanogramId}
                  onChange={handleMacroplanogramChange}
                  macroplanogramList={canFetchMacro ? macroplanogramList : undefined}
                  disabled={!canFetchMacro || isMacroLoading}
                />
                <Row spacing="none" alignmentVertical="bottom">
                  {/*
                    Subcategory selector is only shown for subcategory-scoped
                    programs (WFM/Fresh). GFC scopes items by the Drop Zone
                    (macroplanogram categoryId), so there is no subcategory
                    selector.
                  */}
                  {scope === 'subcategory' && (
                    <>
                      <SubcategorySelector
                        value={subcategoryId}
                        onChange={handleSubcategoryChange}
                        subcategories={subcategories}
                        disabled={!macroplanogramId}
                        label={subcategoryLabel}
                      />
                      {subcategoryId && (
                        <Button
                          type="icon"
                          size="small"
                          onClick={() => setSubcategoryId(undefined)}
                          aria-label={`Clear ${subcategoryLabel.toLowerCase()}`}
                        >
                          <Icon tokens={closeLargeTokens} />
                        </Button>
                      )}
                    </>
                  )}
                </Row>
                <Toggle checked={includeFuture} onChange={setIncludeFuture}>
                  Include Future
                </Toggle>
                <Button onClick={handleSearch} disabled={!canSearch}>
                  Search
                </Button>
              </Row>
              {searchEnabled && isSearching && (
                <Column alignmentHorizontal="center" spacingInset="500">
                  <Loader type="circular" size="large" />
                </Column>
              )}
              {searchEnabled && (
                <Row alignmentHorizontal="start" spacing="300">
                  <Button onClick={() => setIsModalOpen(true)}>Add Items</Button>
                  {selectedIds.size === 0 ? (
                    <DisabledButtonWithTooltip title="Select items to modify" label="Modify" />
                  ) : (
                    <Button type="secondary" onClick={() => setIsModifyModalOpen(true)}>
                      {`Modify (${selectedIds.size})`}
                    </Button>
                  )}
                  {selectedIds.size === 0 ? (
                    <DisabledButtonWithTooltip title="Select items to delete" label="Delete" />
                  ) : (
                    <Button type="secondary" onClick={() => setIsDeleteModalOpen(true)}>
                      {`Delete (${selectedIds.size})`}
                    </Button>
                  )}
                  {isSearching || !itemEntriesResult || itemEntriesResult.length === 0 ? (
                    <DisabledButtonWithTooltip
                      title={isSearching ? 'Loading...' : 'No data to export'}
                      label="Export"
                    />
                  ) : (
                    <Button type="secondary" onClick={handleExportCsv}>
                      Export
                    </Button>
                  )}
                </Row>
              )}
              {searchEnabled && !isSearching && (
                <ItemAssortmentTable
                  assortmentData={itemEntriesResult}
                  isLoading={isSearching}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  subcategories={subcategories}
                  selectedIds={selectedIds}
                  onSelectionChange={setSelectedIds}
                  scopeColumnLabel={subcategoryLabel}
                  showScopeColumn={scope === 'subcategory'}
                />
              )}
              <AddItemsModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSaveSuccess={() => {
                  setToasts((prev) => [
                    ...prev,
                    {
                      id: `add-success-${Date.now()}`,
                      children: 'Items added successfully.',
                      type: 'success' as const,
                      dismissAfter: 5000,
                    },
                  ]);
                  resetBulkUploadState();
                  queryClient.removeQueries(ITEM_ENTRIES_QUERY_FILTER);
                }}
                storeId={storeId ?? ''}
                macroplanogramId={macroplanogramId ?? ''}
                selectedDate={date}
                macroplanogramStartTime={Number(selectedMacro?.startTime) || 0}
                macroplanogramEndTime={Number(selectedMacro?.endTime) || 0}
                subcategories={subcategories}
                existingItems={itemEntriesResult}
                categoryId={macroCategoryId}
              />
              <DeleteItemsModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onDeleteSuccess={() => {
                  setToasts((prev) => [
                    ...prev,
                    {
                      id: `delete-success-${Date.now()}`,
                      children: 'Items deleted successfully.',
                      type: 'success' as const,
                      dismissAfter: 5000,
                    },
                  ]);
                  setSelectedIds(new Set());
                  resetBulkUploadState();
                  queryClient.removeQueries(ITEM_ENTRIES_QUERY_FILTER);
                }}
                storeId={storeId ?? ''}
                macroplanogramId={macroplanogramId ?? ''}
                selectedEntries={selectedEntries}
                subcategories={subcategories}
              />
              <ModifyItemsModal
                isOpen={isModifyModalOpen}
                onClose={() => setIsModifyModalOpen(false)}
                onModifySuccess={() => {
                  setToasts((prev) => [
                    ...prev,
                    {
                      id: `modify-success-${Date.now()}`,
                      children: 'Items modified successfully.',
                      type: 'success' as const,
                      dismissAfter: 5000,
                    },
                  ]);
                  setSelectedIds(new Set());
                  resetBulkUploadState();
                  queryClient.removeQueries(ITEM_ENTRIES_QUERY_FILTER);
                }}
                storeId={storeId ?? ''}
                macroplanogramId={macroplanogramId ?? ''}
                selectedEntries={selectedEntries}
                subcategories={subcategories}
                selectedDate={date}
                macroplanogramStartTime={Number(selectedMacro?.startTime) || 0}
                macroplanogramEndTime={Number(selectedMacro?.endTime) || 0}
                existingItems={itemEntriesResult}
                categoryId={macroCategoryId}
              />
            </Column>
          </Box>
        </div>
        <div style={{ display: activeTab === 'bulkUpload' ? 'block' : 'none' }}>
          <Box spacingInset="300">
            <Column spacing="500">
              {showBulkUploadHint && (
                <Box type="outline" spacingInset="300" backgroundColor="secondary">
                  <Row alignmentVertical="center" widths={['fit', 'fill', 'fit']}>
                    <span style={{ color: 'rgba(0, 115, 187, 0.6)' }}>
                      <Icon tokens={bulbTokens} />
                    </span>
                    <Text>
                      Please upload the complete intended assortment list. Items not included will be flagged for
                      removal.
                    </Text>
                    <Button
                      type="icon"
                      size="small"
                      onClick={() => setShowBulkUploadHint(false)}
                      aria-label="Dismiss hint"
                    >
                      <Icon tokens={closeSmallTokens} />
                    </Button>
                  </Row>
                </Box>
              )}
              <Row alignmentVertical="bottom">
                <StoreSelector selectedStoreId={storeId} onStoreSelect={handleStoreChange} />
                <CustomDatePicker
                  value={date}
                  onChange={handleDateChange}
                  label="Date"
                  disabledDates={disablePastDates}
                />
                <MacroplanogramSelector
                  value={macroplanogramId}
                  onChange={handleMacroplanogramChange}
                  macroplanogramList={macroplanogramList}
                  disabled={!macroplanogramList || macroplanogramList.length === 0}
                />
                <ItemBulkUpload
                  setInputItemEntries={setInputItemEntries}
                  setInputTableFlag={setInputTableFlag}
                  setReviewTableFlag={setReviewTableFlag}
                  setAlertFlag={setAlertFlag}
                  hasMacroplanogramData={!!macroplanogramList}
                  hasMacroplanogramSelected={!!macroplanogramId}
                  reviewTableFlag={reviewTableFlag}
                  inputTableFlag={inputTableFlag}
                  resetKey={bulkUploadResetKey}
                />
                {!reviewTableFlag && !inputTableFlag && (
                  <Tooltip position="top" title="Download template CSV">
                    <Button
                      type="icon"
                      size="small"
                      onClick={handleDownloadTemplate}
                      aria-label="Download template CSV"
                    >
                      <Icon tokens={downloadLargeTokens} />
                    </Button>
                  </Tooltip>
                )}
              </Row>
              <ItemBulkUploadMain
                listOfMacroplanogram={macroplanogramList}
                swapTime={timestamp}
                selectedMacroplanogramId={macroplanogramId}
                inputItemEntries={inputItemEntries}
                inputTableFlag={inputTableFlag}
                setInputTableFlag={setInputTableFlag}
                reviewTableFlag={reviewTableFlag}
                setReviewTableFlag={setReviewTableFlag}
                alertFlag={alertFlag}
                setAlertFlag={setAlertFlag}
                macroplanogramEndTime={Number(selectedMacro?.endTime) || 0}
                onSaveSuccess={handleBulkUploadSaveSuccess}
                onDuplicatesRemoved={handleDuplicatesRemoved}
              />
            </Column>
          </Box>
        </div>
      </Column>
    </Box>
  );
};

export default ItemAssortmentManagement;
